import {
    Collapse,
    createStyles,
    Fab,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import "moment-timezone";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { BidLoadState } from "../../../models/utility/LoadState";
import { IAppDispatch, IAppState } from "../../../store";
import * as ParcelBidStore from "../../../store/ParcelBidStore";
import * as UserStore from "../../../store/UserStore";
import { Numbers } from "../../../models/utility/Numbers";
import posthog from "posthog-js";

type IProps = ParcelBidStore.IParcelBidStoreState &
    UserStore.IUserStoreState &
    typeof ParcelBidStore.actionCreators &
    typeof UserStore.actionCreators &
    WithStyles<typeof styles> & {
        bidIncrement: number;
        canPlaceBid: boolean;
        isLive: boolean;
        parcelId: string;
        lastAcceptedBid: number;
    };

const styles = (theme: Theme) =>
    createStyles({
        bidPopup: {
            [theme.breakpoints.down("sm")]: {
                paddingLeft: theme.spacing(3),
                paddingRight: theme.spacing(3)
            },
            display: "inline-block",
            paddingLeft: theme.spacing(),
            paddingRight: theme.spacing()
        },
        bidPopupClosed: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: `${theme.spacing()}px !important`,
                paddingRight: `${theme.spacing()}px !important`
            },
            paddingLeft: `${theme.spacing(2)}px !important`,
            paddingRight: `${theme.spacing(2)}px !important`
        },
        bidPopupHighest: {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText
        },
        bidPopupOutbid: {
            backgroundColor: theme.palette.background.overlay,
            color: theme.palette.common.white
        },
        fabButtonBid: {
            [theme.breakpoints.down("sm")]: {
                paddingLeft: theme.spacing(9),
                paddingRight: theme.spacing(9)
            },
            display: "block",
            height: theme.spacing(4),
            paddingLeft: theme.spacing(6),
            paddingRight: theme.spacing(6),
            color: theme.palette.common.black
        },
        fabButtonBidWrapper: {
            display: "inline-block",
            textAlign: "center",
            paddingBottom: theme.spacing()
        }
    });

class PlaceBidButton extends React.Component<IProps, {}> {
    public shouldComponentUpdate(prevProps: IProps) {
        if (
            prevProps.bidIncrement !== this.props.bidIncrement ||
            prevProps.canPlaceBid !== this.props.canPlaceBid ||
            prevProps.isLive !== this.props.isLive ||
            prevProps.parcelId !== this.props.parcelId ||
            prevProps.parcelBidDict[prevProps.parcelId] !==
                this.props.parcelBidDict[this.props.parcelId] ||
            prevProps.lastAcceptedBid !== this.props.lastAcceptedBid ||
            prevProps.user !== this.props.user
        ) {
            return true;
        }

        return false;
    }

    public render() {
        const {
            bidIncrement,
            canPlaceBid,
            classes,
            parcelBidDict,
            parcelId,
            user,
            lastAcceptedBid
        } = this.props;

        const parcelBids = parcelBidDict[parcelId] || [];
        const hasPlacedBid = !!(
            user &&
            parcelBids.length > 0 &&
            parcelBids.find((e) => e.userId === user.id)
        );
        const highestBidder = !!(
            user &&
            parcelBids.length > 0 &&
            user.id === parcelBids[parcelBids.length - 1].userId
        );

        const bidCount = parcelBids.length;

        const newBidIncrement = bidCount > 0 ? bidIncrement : 0;
        const nextBid = lastAcceptedBid + newBidIncrement;

        return (
            <div className={classes.fabButtonBidWrapper}>
                {this.props.user && (
                    <>
                        <Collapse
                            in={hasPlacedBid && !highestBidder}
                            mountOnEnter={true}
                            unmountOnExit={true}
                        >
                            <Typography
                                className={`${classes.bidPopup} ${classes.bidPopupOutbid}`}
                                component="div"
                                variant="caption"
                            >
                                You were outbid!
                            </Typography>
                        </Collapse>
                        <Collapse
                            in={hasPlacedBid && highestBidder}
                            mountOnEnter={true}
                            unmountOnExit={true}
                        >
                            <Typography
                                className={`${classes.bidPopup} ${classes.bidPopupHighest}`}
                                component="div"
                                variant="caption"
                            >
                                You have highest bid!
                            </Typography>
                        </Collapse>
                        <Fab
                            className={classes.fabButtonBid}
                            disabled={highestBidder || !canPlaceBid}
                            variant="extended"
                            color="primary"
                            onClick={this.openBidDialog}
                        >
                            Bid {Numbers.toShortFormat(nextBid)}
                        </Fab>
                    </>
                )}
                {!this.props.user && (
                    <Fab
                        className={classes.fabButtonBid}
                        variant="extended"
                        color="primary"
                        onClick={this.redirect}
                    >
                        Login to Bid
                    </Fab>
                )}
            </div>
        );
    }

    private openBidDialog = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        this.props.toggleDialogVisible(true);
        this.props.updateCurrentBid(
            this.props.parcelId,
            BidLoadState.NotSubmitted
        );
    };

    private redirect = () => {
        posthog.capture("ParcelPanel.LoginToBidClicked");
        return this.props.toggleRedirectToLogin(true);
    };
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.parcelBidStoreState, state.userStoreState);
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign(
            {},
            ParcelBidStore.actionCreators,
            UserStore.actionCreators
        ),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(PlaceBidButton)
);
